import { ReactElement } from 'react';

import { Timestamp } from 'firebase/firestore';

import Avatar from '@mui/material/Avatar';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined';
import InfoIcon from '@mui/icons-material/Info';
import PendingIcon from '@mui/icons-material/Pending';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonOffIcon from '@mui/icons-material/PersonOff';

import ApplicationRequest, { REQUEST_STATUSES } from '../../types/ApplicationRequest';
import { DisplayTime, FormatPhoneNumber } from '../../utils/utils';
import GratiDirectoryItem from '../common/GratiDirectoryItem';

interface ApplicationRequestLineItemProps {
  applicationRequest: ApplicationRequest;
  isDividerDisplayed?: boolean;
}

export default function ApplicationRequestLineItem({
  applicationRequest,
  isDividerDisplayed = true,
}: ApplicationRequestLineItemProps): ReactElement {
  const createTime =
    applicationRequest.createTime instanceof Timestamp ? applicationRequest.createTime : Timestamp.now();

  const StyledDateTime = (): ReactElement => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const requestDate = (applicationRequest.createTime as Timestamp).toDate();
    
    const monthText = requestDate.toLocaleString('default', { month: 'short', timeZone: tz });
    const dayText = requestDate.toLocaleString('default', { day: 'numeric', timeZone: tz });
    const timeString = DisplayTime(createTime, tz, 'h:mm a');

    return (
      <Avatar
        variant="rounded"
        sx={{
          bgcolor: 'transparent',
          color: 'text.primary',
          p: 0.5,
          height: '4rem',
          width: '4rem'
        }}
      >
        <Stack direction="column" sx={{
          alignItems: "center"
        }}>
          <Typography variant="titleMedium" sx={{ py: 0, my: 0, lineHeight: 1 }}>
            {monthText} {dayText}
          </Typography>
          <Typography variant="labelMedium" sx={{ pt: 1, my: 0, lineHeight: 1 }}>
            {timeString}
          </Typography>
        </Stack>
      </Avatar>
    );
  };

  const StyledContactInfo = (): ReactElement => {
    return (
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }} sx={{
        alignItems: { xs: 'flex-start', sm: 'center' }
      }}>
        <Typography component="span" variant="bodyLarge" sx={{ py: 0, my: 0 }}>
          {applicationRequest.email}
        </Typography>
        <Typography component="span" variant="bodyMedium" sx={{ py: 0, my: 0 }}>
          {FormatPhoneNumber(applicationRequest.phone)}
        </Typography>
      </Stack>
    );
  };

  const getStatusClassName = () => {
    switch (applicationRequest.status) {
      case REQUEST_STATUSES.REQUEST_FOR_INFO:
      case REQUEST_STATUSES.PENDING:
      case REQUEST_STATUSES.APPROVED:
        return 'active';
      case REQUEST_STATUSES.DUPLICATE:
      case REQUEST_STATUSES.ALREADY_MEMBER:
        return 'archived';
      case REQUEST_STATUSES.REJECTED:
        return 'deleted';
      default:
        return 'active';
    }
  };

  const StyledStatus = (): ReactElement => {
    const getStatusIcon = () => {
      switch (applicationRequest.status) {
        case REQUEST_STATUSES.REQUEST_FOR_INFO:
          return <InfoIcon />;
        case REQUEST_STATUSES.PENDING:
          return <PendingIcon />;
        case REQUEST_STATUSES.APPROVED:
          return <PersonAddIcon />;
        case REQUEST_STATUSES.DUPLICATE:
          return <ContentCopyIcon />;
        case REQUEST_STATUSES.REJECTED:
          return <PersonOffIcon />;
        case REQUEST_STATUSES.ALREADY_MEMBER:
          return <PersonIcon />;
        default:
          return <HelpOutlinedIcon />;
      }
    };

    const getStatusColor = () => {
      switch (applicationRequest.status) {
        case REQUEST_STATUSES.REQUEST_FOR_INFO:
          return 'info.main';
        case REQUEST_STATUSES.PENDING:
          return 'warning.main';
        case REQUEST_STATUSES.APPROVED:
          return 'success.main';
        case REQUEST_STATUSES.DUPLICATE:
          return 'grey.500';
        case REQUEST_STATUSES.REJECTED:
          return 'error.main';
        case REQUEST_STATUSES.ALREADY_MEMBER:
          return 'primary.main';
        default:
          return 'grey.500';
      }
    };

    const getStatusTooltip = () => {
      switch (applicationRequest.status) {
        case REQUEST_STATUSES.REQUEST_FOR_INFO:
          return 'Request for Information';
        case REQUEST_STATUSES.PENDING:
          return 'Pending Review';
        case REQUEST_STATUSES.APPROVED:
          return 'Approved - Invitation Sent';
        case REQUEST_STATUSES.DUPLICATE:
          return 'Duplicate Application';
        case REQUEST_STATUSES.REJECTED:
          return 'Rejected';
        case REQUEST_STATUSES.ALREADY_MEMBER:
          return 'Already a Member';
        default:
          return 'Unknown Status';
      }
    };

    return (
      <Tooltip title={getStatusTooltip()}>
        <Avatar sx={{ bgcolor: getStatusColor() }}>
          {getStatusIcon()}
        </Avatar>
      </Tooltip>
    );
  };

  return (
    <GratiDirectoryItem
      divider={isDividerDisplayed}
      className={getStatusClassName()}
      slots={{
        icon: <StyledStatus />,
        avatar1: <StyledDateTime />,
        primary: applicationRequest.name,
        secondary: <StyledContactInfo />
      }}
      slotProps={{
        icon: {
          hideBreakpoint: 'xs'
        }
      }}
    />
  );
}
